import { getRandomId as getRandomNodeId } from "@craftjs/utils";
import { useEditor, useNode } from "@craftjs/core";
import React, { useState, useContext, useMemo, useEffect } from "react";
import { Resizable, RichTextInput } from "../../input";
import LoaderSmall from "../../loader/loadersmall";
import { GrigoraDashboardContext } from "../../dashboard/context";
import VideoPicker from "../../input/video";
import icon from "./icon";
import GrigoraCSSRenderer from "./../css-render";
import { VideoSettings } from "./settings";
import classnames from "classnames";
import { getDynamicCSS, getSupportProps } from "../../block-supports";
import { remove_prefix } from "../../helpers";
import { VideoPickerUIKit } from "../../ui-kit";
import { CMSVideoSettings } from "./cms-settings";
import CMSIcon from "./cms-icon";
import { getBlobByURL, isBlobURL, waitForElement } from "../../dom";
import { blocksLibrary } from "..";
import getVideoId from "get-video-id";

var VIDEO_SIZES = [1920, 1600, 1080, 800, 500, 300];

export const Video = (props) => {
  const {
    projectID,
    screen,
    uploadProjectVideo,
    editorDocument = document,
  } = useContext(GrigoraDashboardContext) ?? {};

  const {
    query: { node, parseFreshNode },
    actions: {
      add,
      delete: deleteCraftJSNode,
      selectNode,
      setProp: setPropGlobal,
    },
  } = useEditor();

  const {
    id,
    connectors: { connect, drag },
    selected,
    actions: { setProp },
    dom,
  } = useNode((state) => ({
    id: state.id,
    selected: state.events.selected,
    dragged: state.events.dragged,
    dom: state.dom,
  }));

  const [srcModal, setSrcModal] = useState(false);

  const {
    posterSrc,
    posterSrcMode,
    src,
    srcMode,
    srcActionTrigger,
    caption,
    width,
    height,
    lazyLoad = true,
    altText,
    title,
    muted = false,
    autoplay = false,
    showControls = true,
    controlsList,
    preload,
    loop,
    disablePictureInPicture,
  } = props;

  var blockProps = getSupportProps({
    id,
    ...props,
  });

  const wrapperClass = classnames(
    {
      "g-video": true,
      [`video-${id}`]: id,
    },
    blockProps.className,
  );

  const videosrc = useMemo(() => {
    if (srcMode === "cdn" && src) {
      return `https://cdn.grigora.co/projects/${projectID}/${remove_prefix(
        src?.sk,
        "video-",
      )}`;
    } else if (srcMode === "link") {
      return src;
    } /* only in editor */ else if (srcMode === "preview") {
      return src;
    }
    return undefined;
  }, [srcMode, src]);

  // Effect to handle YouTube link conversion
  useEffect(() => {
    if (srcMode === "link" && src) {
      const videoInfo = getVideoId(src);
      if (videoInfo && videoInfo.service === "youtube") {
        // Create new YouTube block
        const nodeID = getRandomNodeId();
        const freshNode = {
          id: nodeID,
          data: {
            type: blocksLibrary["grigora/youtube"],
            props: {
              url: src,
              showControls: true,
            },
          },
        };
        const nodeToAdd = parseFreshNode(freshNode).toNode();

        // Get parent and index for insertion
        const parents = node(id).ancestors();
        var node_parent = undefined;
        var node_index = undefined;
        if (parents && parents.length > 0) {
          node_parent = parents[0];
        }
        if (node_parent) {
          let parentChildrens = node(node_parent).childNodes();
          node_index = parentChildrens.indexOf(id);
        }

        // Add new YouTube block and delete current video block
        add(nodeToAdd, node_parent, node_index);
        deleteCraftJSNode(id);
        selectNode(nodeID);
      }
    }
  }, [srcMode, src]);

  useEffect(() => {
    // Upload Action Trigger
    if (srcActionTrigger === "upload") {
      if (!isBlobURL(src)) {
        return;
      }

      const videoFile = getBlobByURL(src);

      uploadProjectVideo?.(videoFile)
        .then((newVideo) => {
          setProp((props) => {
            props.src = {
              ...newVideo,
              projectID: newVideo.projectID ? newVideo.projectID : projectID,
            };
            props.srcMode = "cdn";
          });
        })
        .catch((err) => {
          setProp((props) => {
            props.src = undefined;
            props.srcMode = undefined;
          });
        });
    }
  }, [srcActionTrigger]);

  return (
    <figure
      {...blockProps}
      ref={(ref) => connect(drag(ref))}
      className={wrapperClass}
      data-grigora-block="true"
      data-grigora-block-id={id}
    >
      <style>{`${getDynamicCSS(id, props)}`}</style>
      <style>{`${GrigoraCSSRenderer.blockCSS__Video(id, props)}`}</style>
      <VideoPickerUIKit
        src={src}
        srcMode={srcMode}
        onSrcChange={(val) => {
          setProp((props) => {
            props.src = val;
          });
        }}
        onSrcModeChange={(val) => {
          setProp((props) => {
            props.srcMode = val;
          });
        }}
        opened={srcModal}
        setOpened={setSrcModal}
      />
      {srcMode === undefined && !src ? (
        <div className="text-gray-500 text-center">
          <div
            onClick={() => {
              setSrcModal(true);
            }}
            contentEditable={false}
            className="add-video-button"
          >
            Add a Video
          </div>
        </div>
      ) : (
        <>
          {srcMode === "preview" ? (
            <>
              <div className="absolute top-[40%] left-[45%] z-50">
                <LoaderSmall />
              </div>
              <video
                className="video-frame"
                src={videosrc}
                alt={`preview video`}
              ></video>
            </>
          ) : (
            <>
              <Resizable
                className="resizable"
                nodeWidth={width}
                nodeHeight={height}
                onChange={(newWidth, newHeight) => {
                  setProp((props) => {
                    props.width = newWidth;
                    props.height = newHeight;
                  }, 300);
                }}
                useDynamicDimensions={true}
                lockAspectRatio={true}
                defaultSize={{
                  width: "fit-content",
                  height: "fit-content",
                }}
                active={selected}
                onClick={(e) => {
                  switch (e.detail) {
                    case 1:
                      break;
                    case 2:
                      setSrcModal(true);
                      e.preventDefault();
                      break;
                    case 3:
                      break;
                  }
                }}
              >
                <video
                  loading={lazyLoad ? "lazy" : "eager"}
                  decoding={"async"}
                  title={title}
                  className="video-frame"
                  width={srcMode === "cdn" ? src?.width : undefined}
                  height={srcMode === "cdn" ? src?.height : undefined}
                  src={videosrc}
                  autoPlay={autoplay ? "autoplay" : undefined}
                  muted={muted ? "muted" : undefined}
                  controlsList={
                    controlsList
                      ? controlsList.map((control) => {
                          return control.value;
                        })
                      : undefined
                  }
                  poster={
                    posterSrcMode === "cdn"
                      ? posterSrc?.og
                        ? `https://cdn.grigora.co/projects/${projectID}/${posterSrc?.og}`
                        : `https://cdn.grigora.co/projects/${projectID}/${posterSrc?.name}.${posterSrc?.extension}`
                      : posterSrcMode === "link"
                      ? posterSrc
                      : undefined
                  }
                  preload={preload ? preload : undefined}
                  loop={loop ? "loop" : undefined}
                  controls={showControls ? "controls" : undefined}
                  disablePictureInPicture={
                    disablePictureInPicture
                      ? disablePictureInPicture
                      : undefined
                  }
                  alt={`${
                    altText ? altText : "No alt attribute set on the video."
                  }`}
                ></video>
              </Resizable>
            </>
          )}
          {(caption || selected) && (
            <figcaption>
              <RichTextInput
                value={caption}
                onChange={(e, historyMergeAction) => {
                  if (historyMergeAction) {
                    historyMergeAction.setProp(
                      id,
                      (props) => {
                        props.caption = e;
                        return props;
                      },
                      500,
                    );
                    return;
                  }
                  setProp((props) => {
                    props.caption = e;
                    return props;
                  }, 500);
                }}
                tagName={"span"}
                placeholder={"Caption..."}
                onSplit={(newValue, oldValue, historyMergeAction) => {
                  const parents = node(id).ancestors();
                  const nodeID = getRandomNodeId();
                  const freshNode = {
                    id: nodeID,
                    data: {
                      type: blocksLibrary["grigora/text"],
                      props: {
                        ...(newValue.trim() ? props : {}),
                        content: newValue.trim(),
                      },
                    },
                  };
                  const nodeToAdd = parseFreshNode(freshNode).toNode();
                  var node_parent = undefined;
                  var node_index = undefined;
                  if (parents && parents.length > 0) {
                    node_parent = parents[0];
                  }
                  if (node_parent) {
                    let parentChildrens = node(node_parent).childNodes();
                    node_index = parentChildrens.indexOf(id) + 1;
                  }

                  if (historyMergeAction) {
                    historyMergeAction.add(nodeToAdd, node_parent, node_index);
                  } else {
                    add(nodeToAdd, node_parent, node_index);
                  }

                  selectNode(nodeID);
                  waitForElement(
                    editorDocument,
                    ".text-" + nodeID + " .grigora-richtext-input__field",
                  ).then((elm) => {
                    elm.focus();
                  });
                }}
              />
            </figcaption>
          )}
        </>
      )}
    </figure>
  );
};

// To Avoid Mangling in Production
Video.displayName = "Video";

Video.craft = {
  name: "Video",
  props: {},
  related: {
    settings: VideoSettings,
    CMSsettings: CMSVideoSettings,
    icon: icon,
    CMSIcon: CMSIcon,
    supports: [
      "grigora/entrance-animations",
      "grigora/css-transform-animations",
      "grigora/mouse-movement",
      "grigora/visibility",
      "grigora/position",
      "grigora/sticky",
      "grigora/filter",
      "grigora/custom-class",
      "grigora/custom-id",
    ],
    category: "miscellaneous",
    keywords: ["Video", "Movie"],
  },
  custom: {
    name: "Video",
    slug: "grigora/video",
  },
};
